import * as Sentry from "@sentry/react";
import cx from "classnames";
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Icon from "../../common/components/Icon/Icon";
import { addPayment, getPaymentDetails, setCurrentPageProgress } from "../api/ApiAppForm";
import AFDirectDebitForm from "../components/AFDirectDebitForm";
import AFLayout from "../components/AFLayout";
import AFNavigation from "../components/AFNavigation";
import AFSecure from "../components/AFSecure";
import { PageType } from "../enums/PageType";
import useAFForm from "../hooks/useAFForm";
import { IPageInfo, IPaymentDetails } from "../interfaces/IAppFormData";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import styles from "../scss/AFPayment.module.scss";
import { redirectTo404Page } from "../shared/AppFormHelper";

interface IAFPaymentPage {
    applicationId: string;
    type: string;
}

const AFPaymentPage: React.FC = () => {
    const history = useHistory();
    const params = useParams<IAFPaymentPage>();
    const applicationId = params.applicationId;
    const paymentType = params.type;
    const [isReady, setIsReady] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState<IPaymentDetails>();
    const [isInErrorState, setIsInErrorState] = useState(false);

    const { selectors } = useContext(AppFormControllerContext);
    const { checkValidApplication } = useAFForm();

    const handleNextPage = (): void => {
        addPayment(selectors.getApplicationId(), selectors.getQuoteGroupId(), {
            paymentMethod: "CreditCard",
            debitDetails: {}
        })
            .then(() => {
                const pageInfo: IPageInfo = {
                    pageType: PageType.Address,
                    currentClientNumber: 1
                }
                setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo
                )
                history.push(`/app-form/submit/${applicationId}/applicant1/address`);
            })
            .catch(() => {
                setIsInErrorState(true);
            });
    };

    const handleLabel = (): string => {
        return paymentType === "debit" ? "direct debit" : "credit card";
    };

    const handleBack = (e): void => {
        e.preventDefault();
        setIsReady(false);
        history.push(`/app-form/submit/${applicationId}/payment`);
    };

    const handleGetDetails = (): void => {
        setPaymentDetails(null);
        getPaymentDetails(selectors.getApplicationId(), selectors.getQuoteGroupId())
            .then((res) => {
                setPaymentDetails(res);
            })
            .catch(() => {
                setIsInErrorState(true);
            });
    };

    const checkValidType = (type) => {
        if (type !== "credit" && type !== "debit") redirectTo404Page();
    };

    useEffect(() => {
        checkValidApplication(applicationId, selectors);
        checkValidType(paymentType);
        if (!isReady) {
            handleGetDetails();
        }
    }, [isReady]);

    useEffect(() => {
        const keyDownHandler = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                handleBack(event);
            }
        };

        document.addEventListener("keydown", keyDownHandler);
        return () => {
            document.removeEventListener("keydown", keyDownHandler);
        };
    }, []);

    useEffect(() => {
        if (typeof Sentry !== 'undefined' && isInErrorState) {
            Sentry.captureMessage(`Error occured for ApplicationId ${applicationId} on the Payment page. The applicants chose the ${paymentType} payment option.`, "error");
        }
    }, [isInErrorState]);

    return (
        <AFLayout isInErrorState={isInErrorState} currentPage={PageType.Payment}>
            <div className={cx(styles["af-payment"], styles["layout"])}>
                <div className={styles["back-btn-wrapper"]}>
                    <AFSecure />
                </div>

                <div
                    className={cx(
                        styles["af-payment-container"],
                        styles["layout-flex-column--centered"]
                    )}>
                        <h2
                            className={cx(
                                styles["payment-title-label"],
                            )}>
                            Payment: {handleLabel()}
                        </h2>
                    {paymentType === "debit" ? (
                        <AFDirectDebitForm data={paymentDetails} handleBack={handleBack} />
                    ) : (
                        <div className={styles["af-payment"]}>
                            <p className={cx(styles["af-payment-info"], styles["mini"])}>
                                Your chosen insurer will contact you soon to request your
                                credit card details.
                            </p>

                            <div className={styles["af-payment-option"]}>
                                <Icon
                                    alt="alert-icon"
                                    className={styles["af-payment-icon"]}
                                    size="xl"
                                    id="AlertInfo"
                                />
                                <p className={styles["af-payment-info"]}>
                                    Your complimentary interim cover will begin once your
                                    credit card details have been provided to your
                                    insurer. If you would prefer immediate interim cover,
                                    please select the direct debit option.
                                </p>
                            </div>
                            <AFNavigation
                                handleBack={handleBack}
                                skipIsVisible={false}
                                forwardIsVisible={true}
                                forwardIsDisabled={false}
                                forwardType="button"
                                onClick={handleNextPage}
                                forwardText={"Next"}
                            />
                        </div>
                    )}
                </div>
            </div>
        </AFLayout>
    );
};

export default AFPaymentPage;
